import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  PackagingRuleProfile,
  PackagingRuleRow,
  ApprovedCartonListItem,
  ApprovedCartonsProfileMeta,
} from '../../services/api/packagingRuleService';
import {
  fetchPackagingRuleList,
  resolvePackagingRule,
  createPackagingRule,
  supersedePackagingRule,
  deletePackagingRule,
  loadApprovedCartonsForScope,
  applyApprovedCartonsChange,
  upsertApprovedCartonOverride,
} from '../thunks/packagingRuleThunks';

export interface PackagingRuleFilters {
  scopePathPrefix: string;
  scopeLevel: number | null;     // null = any
  includeExpired: boolean;
}

/**
 * Spec pack-analysis-approved-cartons-admin — sub-state for the Approved
 * Cartons admin page. `profileMeta=null` means the selected scope has no
 * active profile (empty-state with Create Profile CTA).
 */
export interface ApprovedCartonsAdminState {
  profileMeta: ApprovedCartonsProfileMeta | null;
  approvedCartons: ApprovedCartonListItem[];
  status: 'idle' | 'loading' | 'success' | 'failure';
  lastError: string | null;
}

export interface PackagingRuleState {
  list: {
    items: PackagingRuleRow[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    isLoading: boolean;
    error: string | null;
  };
  /** Cache keyed by `packagingRuleCacheKey()`. `null` = fetched + missing. */
  resolved: Record<string, PackagingRuleProfile | null>;
  filters: PackagingRuleFilters;
  approvedCartonsAdmin: ApprovedCartonsAdminState;
}

const initialState: PackagingRuleState = {
  list: {
    items: [],
    pageIndex: 1,
    pageSize: 50,
    totalCount: 0,
    isLoading: false,
    error: null,
  },
  resolved: {},
  filters: {
    scopePathPrefix: '',
    scopeLevel: null,
    includeExpired: false,
  },
  approvedCartonsAdmin: {
    profileMeta: null,
    approvedCartons: [],
    status: 'idle',
    lastError: null,
  },
};

const packagingRuleSlice = createSlice({
  name: 'packagingRule',
  initialState,
  reducers: {
    clearError: (state) => { state.list.error = null; },
    setFilter: <K extends keyof PackagingRuleFilters>(
      state: PackagingRuleState,
      action: PayloadAction<{ key: K; value: PackagingRuleFilters[K] }>
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    clearFilters: (state) => { state.filters = { ...initialState.filters }; },
    invalidateResolved: (state) => { state.resolved = {}; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPackagingRuleList.pending, (state) => {
        state.list.isLoading = true;
        state.list.error = null;
      })
      .addCase(fetchPackagingRuleList.fulfilled, (state, action) => {
        state.list.isLoading = false;
        state.list.items = action.payload.items;
        state.list.pageIndex = action.payload.pageIndex;
        state.list.pageSize = action.payload.pageSize;
        state.list.totalCount = action.payload.totalCount;
      })
      .addCase(fetchPackagingRuleList.rejected, (state, action) => {
        state.list.isLoading = false;
        state.list.error = action.payload as string;
      });

    builder
      .addCase(resolvePackagingRule.fulfilled, (state, action) => {
        state.resolved[action.payload.key] = action.payload.data;
      });

    // Successful writes invalidate the resolved cache so consumers refetch.
    builder
      .addCase(createPackagingRule.fulfilled, (state) => { state.resolved = {}; })
      .addCase(supersedePackagingRule.fulfilled, (state) => { state.resolved = {}; })
      .addCase(deletePackagingRule.fulfilled, (state, action) => {
        state.resolved = {};
        state.list.items = state.list.items.filter((x) => x.id !== action.payload);
        state.list.totalCount = Math.max(0, state.list.totalCount - 1);
      });

    // Approved Cartons admin sub-state
    builder
      .addCase(loadApprovedCartonsForScope.pending, (state) => {
        state.approvedCartonsAdmin.status = 'loading';
        state.approvedCartonsAdmin.lastError = null;
      })
      .addCase(loadApprovedCartonsForScope.fulfilled, (state, action) => {
        state.approvedCartonsAdmin.status = 'success';
        state.approvedCartonsAdmin.profileMeta = action.payload.profileMeta;
        state.approvedCartonsAdmin.approvedCartons = action.payload.approvedCartons;
      })
      .addCase(loadApprovedCartonsForScope.rejected, (state, action) => {
        state.approvedCartonsAdmin.status = 'failure';
        state.approvedCartonsAdmin.lastError =
          (action.payload as string) ?? action.error.message ?? 'Failed to load approved cartons';
      });

    // Apply add / remove / reorder change — the thunk returns the refreshed
    // approved-cartons list (fetched directly from the new profile id, NOT
    // via resolve, to bypass the 60s BE resolver cache). Update state from
    // the response so the UI reflects the mutation immediately without a
    // separate reload dispatch.
    builder
      .addCase(applyApprovedCartonsChange.fulfilled, (state, action) => {
        state.approvedCartonsAdmin.profileMeta = action.payload.refreshed.profileMeta;
        state.approvedCartonsAdmin.approvedCartons = action.payload.refreshed.approvedCartons;
        state.approvedCartonsAdmin.status = 'success';
        state.approvedCartonsAdmin.lastError = null;
      })
      .addCase(applyApprovedCartonsChange.rejected, (state, action) => {
        state.approvedCartonsAdmin.lastError =
          (action.payload as string) ?? action.error.message ?? 'Failed to update approved cartons';
      });

    // Scope-override upsert (Spec scope-override extension) — refreshes the
    // approved-cartons list from the chained inline fetch in the thunk.
    builder
      .addCase(upsertApprovedCartonOverride.fulfilled, (state, action) => {
        state.approvedCartonsAdmin.profileMeta = action.payload.refreshed.profileMeta;
        state.approvedCartonsAdmin.approvedCartons = action.payload.refreshed.approvedCartons;
        state.approvedCartonsAdmin.status = 'success';
        state.approvedCartonsAdmin.lastError = null;
      })
      .addCase(upsertApprovedCartonOverride.rejected, (state, action) => {
        state.approvedCartonsAdmin.lastError =
          (action.payload as string) ?? action.error.message ?? 'Failed to save carton override';
      });
  },
});

export const { clearError, setFilter, clearFilters, invalidateResolved } = packagingRuleSlice.actions;
export default packagingRuleSlice.reducer;
